/**
 * @fileoverview Dispatcher that maps a selected SectionId to its property editor. This is the
 * single editor surface rendered by BOTH the Section list and the Canvas selection panel so the
 * two surfaces expose identical attributes and write to one model (G-PARITY-01, G-PARITY-02).
 * @module ui-admin/template-builder/documentSectionEditors/DocumentSectionEditor
 *
 * Responsibilities: - Resolve a SectionId to the correct per-section editor component.
 *                   - Apply the permit special-case (bodyText → PermitConfigEditor for permits).
 *                   - Fall back to GenericSectionEditor for sections without a bespoke form.
 * Dependencies:     - ./SectionEditors (the editors); ./types (DocumentConfig/SectionConfig).
 * Architecture:     - Presentation-only; controlled via (config, onChange). Extracted from the two
 *                     builder pages' inline editorMap (G-DRY-01).
 * Notes:            - selectedSection is used only for the generic fallback's label/description.
 */

import {
  CoverPageSectionEditor, HeaderSectionEditor, RecipientBlockEditor, SubjectLineEditor,
  SalutationEditor, BodyTextEditor, MetadataSectionEditor, ViolationsSectionEditor,
  PhotoSectionEditor, CommentsSectionEditor, ContactBlockEditor, SignatureSectionEditor,
  LegalTextEditor, FooterSectionEditor, StaticContentEditor, PermitConfigEditor,
  GenericSectionEditor,
} from './SectionEditors';
import type { DocumentConfig, SectionConfig, SectionId } from './types';

/**
 * @component DocumentSectionEditor
 * Purpose:     Render the property editor for the currently selected document section.
 * Props:       sectionId, selectedSection, config, onChange, advancedMode?
 * Behavior:    Pure dispatch over sectionId; controlled editors patch config via onChange.
 * Data Source: local (in-memory builder config).
 * Constraints: presentation-only; no API calls.
 * Performance: trivial.
 */
export function DocumentSectionEditor({
  sectionId,
  selectedSection,
  config,
  onChange,
  advancedMode,
  hideLogoControls,
}: {
  sectionId: SectionId;
  selectedSection: SectionConfig;
  config: DocumentConfig;
  onChange: (c: DocumentConfig) => void;
  advancedMode?: 'standard' | 'advancedLinked';
  /** Suppress logo visibility/position toggles when a richer logo editor (canvas slots) is present. */
  hideLogoControls?: boolean;
}) {
  if (config.documentType === 'permit' && sectionId === 'bodyText') {
    return <PermitConfigEditor config={config} onChange={onChange} />;
  }

  switch (sectionId) {
    case 'cover': return <CoverPageSectionEditor config={config} onChange={onChange} hideLogoControls={hideLogoControls} />;
    case 'header': return <HeaderSectionEditor config={config} onChange={onChange} hideLogoControls={hideLogoControls} />;
    case 'recipientBlock': return <RecipientBlockEditor config={config} onChange={onChange} />;
    case 'subjectLine': return <SubjectLineEditor config={config} onChange={onChange} />;
    case 'salutation': return <SalutationEditor config={config} onChange={onChange} />;
    case 'bodyText': return <BodyTextEditor config={config} onChange={onChange} advancedMode={advancedMode} />;
    case 'metadata': return <MetadataSectionEditor config={config} onChange={onChange} />;
    case 'violations': return <ViolationsSectionEditor config={config} onChange={onChange} />;
    case 'photos': return <PhotoSectionEditor config={config} onChange={onChange} />;
    case 'comments': return <CommentsSectionEditor config={config} onChange={onChange} />;
    case 'contactBlock': return <ContactBlockEditor config={config} onChange={onChange} />;
    case 'signatures': return <SignatureSectionEditor config={config} onChange={onChange} />;
    case 'legal': return <LegalTextEditor config={config} onChange={onChange} />;
    case 'footer': return <FooterSectionEditor config={config} onChange={onChange} />;
    case 'staticContent': return <StaticContentEditor config={config} onChange={onChange} />;
    default: return <GenericSectionEditor section={selectedSection} />;
  }
}
